import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { seoArticles, shoppingGuideIntro } from '@/mocks/articles';

export default function ShoppingGuide() {
  return (
    <div className="min-h-screen bg-background-50 font-body">
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative w-full h-[380px] md:h-[480px] flex items-center justify-center overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url("${shoppingGuideIntro.image}")` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/55" />
          <div className="relative z-10 w-full text-center px-4 max-w-4xl mx-auto">
            <span className="inline-block px-3 py-1 rounded-full border border-white/30 text-white/80 text-xs font-medium mb-4">
              Shopping Guide
            </span>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
              {shoppingGuideIntro.title}
            </h1>
            <p className="text-white/75 text-sm md:text-base max-w-2xl mx-auto font-light leading-relaxed">
              {shoppingGuideIntro.subtitle}
            </p>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-4 mb-10">
              <div>
                <p className="text-xs font-semibold text-accent-600 uppercase tracking-widest mb-2">Expert Guides</p>
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
                  All Articles
                </h2>
              </div>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                Back to Home
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {/* Original guide card */}
              <Link
                to="/gtbuy-spreadsheet-complete-guide"
                className="group bg-background-100 rounded-xl overflow-hidden hover:bg-background-100/80 transition-colors cursor-pointer"
              >
                <div className="w-full h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20minimal%20workspace%20with%20laptop%20displaying%20spreadsheet%20dashboard%20clean%20desk%20setup%20warm%20ambient%20lighting%20organized%20professional%20environment%20with%20notebook%20and%20green%20plant%20editorial%20tech%20photography%20warm%20neutral%20tones%20high%20detail%20sophisticated%20composition&width=800&height=600&seq=guide-card-complete&orientation=landscape"
                    alt="GTBuy Spreadsheet Complete Guide"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <span className="text-xs text-foreground-500">2026 Guide</span>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mt-1 mb-2 line-clamp-2">
                    GTBuy Spreadsheet Explained: The Ultimate 2026 Setup Guide
                  </h3>
                  <p className="text-foreground-600 text-xs leading-relaxed line-clamp-2">
                    A comprehensive walkthrough covering setup, features, comparisons, and everything you need to streamline your online shopping workflow.
                  </p>
                </div>
              </Link>

              {seoArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/shopping-guide/${article.slug}`}
                  className="group bg-background-100 rounded-xl overflow-hidden hover:bg-background-100/80 transition-colors cursor-pointer"
                >
                  <div className="w-full h-48 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.imageAlt}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5">
                    <span className="text-xs text-foreground-500">{article.date}</span>
                    <h3 className="font-heading text-base font-semibold text-foreground-950 mt-1 mb-2 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-foreground-600 text-xs leading-relaxed line-clamp-2">
                      {article.summary}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
              Ready to Start Tracking Smarter?
            </h2>
            <p className="text-foreground-600 text-sm mb-6 max-w-xl mx-auto">
              Apply the strategies from our guides and experience the difference that spreadsheet-based order tracking makes in your daily shopping workflow.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-4-line w-4 h-4 flex items-center justify-center"></i>
              Return to Home
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}